import { useEffect, useState } from 'react'
import { ApiError, api } from '../lib/api'
import type {
  AdminActiveShareLimitMode,
  AdminPlanPolicy,
  AdminPlanPolicyAudit,
  AdminPlanPolicyUpdatePayload,
  AdminPolicyDurationUnit,
  AdminPolicyPlanCode,
  AdminShareDownloadPolicy,
} from '../lib/adminTypes'
import './AdminPolicyPanel.css'

type PolicyDraft = {
  weeklyFileUnlimited: boolean
  weeklyFileRegistrationLimit: string
  maxStoredFilesUnlimited: boolean
  maxStoredFileSongs: string
  maxSegnosUnlimited: boolean
  maxSegnosPerSong: string
  dataRetentionAmount: string
  dataRetentionUnit: AdminPolicyDurationUnit
  audioRetentionAmount: string
  audioRetentionUnit: AdminPolicyDurationUnit
  activeShareLimitMode: AdminActiveShareLimitMode
  fixedActiveShareLimit: string
  shareRetentionAmount: string
  shareRetentionUnit: AdminPolicyDurationUnit
  albumSharingEnabled: boolean
  ownerAdsEnabled: boolean
}

const PLAN_ORDER: AdminPolicyPlanCode[] = [
  'OPEN_BETA', 'FREE', 'CHAPEL', 'CONCERT', 'PROFESSIONAL', 'UNLIMITED',
]

const PLAN_LABEL: Record<AdminPolicyPlanCode, string> = {
  OPEN_BETA: 'OpenBeta',
  FREE: 'Free',
  CHAPEL: 'Chapel',
  CONCERT: 'Concert',
  PROFESSIONAL: 'Professional',
  UNLIMITED: 'Unlimited (운영용)',
}

function toDraft(policy: AdminPlanPolicy): PolicyDraft {
  return {
    weeklyFileUnlimited: policy.weeklyFileRegistrationLimit == null,
    weeklyFileRegistrationLimit: policy.weeklyFileRegistrationLimit?.toString() ?? '',
    maxStoredFilesUnlimited: policy.maxStoredFileSongs == null,
    maxStoredFileSongs: policy.maxStoredFileSongs?.toString() ?? '',
    maxSegnosUnlimited: policy.maxSegnosPerSong == null,
    maxSegnosPerSong: policy.maxSegnosPerSong?.toString() ?? '',
    dataRetentionAmount: policy.dataRetentionAmount.toString(),
    dataRetentionUnit: policy.dataRetentionUnit,
    audioRetentionAmount: (policy.audioRetentionAmount ?? policy.dataRetentionAmount).toString(),
    audioRetentionUnit: policy.audioRetentionUnit ?? policy.dataRetentionUnit,
    activeShareLimitMode: policy.activeShareLimitMode,
    fixedActiveShareLimit: policy.fixedActiveShareLimit?.toString() ?? '',
    shareRetentionAmount: policy.shareRetentionAmount?.toString() ?? '',
    shareRetentionUnit: policy.shareRetentionUnit ?? 'MONTHS',
    albumSharingEnabled: policy.albumSharingEnabled,
    ownerAdsEnabled: policy.ownerAdsEnabled,
  }
}

function positiveInt(value: string, label: string): number {
  const parsed = Number(value)
  if (!Number.isInteger(parsed) || parsed <= 0) throw new Error(`${label}은(는) 1 이상의 정수로 입력해 주세요.`)
  return parsed
}

function adminError(error: unknown): string {
  if (error instanceof Error && !(error instanceof ApiError)) return error.message
  if (error instanceof ApiError && error.status === 403) return '요금제 정책을 변경할 권한이 없습니다.'
  return '요금제 정책을 처리하지 못했습니다. 잠시 후 다시 시도해 주세요.'
}

function formatDate(value: string): string {
  const date = new Date(value)
  return Number.isNaN(date.getTime()) ? value : date.toLocaleString('ko-KR')
}

export function AdminPolicyPanel() {
  const [policies, setPolicies] = useState<AdminPlanPolicy[]>([])
  const [drafts, setDrafts] = useState<Record<string, PolicyDraft>>({})
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState<AdminPolicyPlanCode | null>(null)
  const [message, setMessage] = useState('')
  const [auditPlan, setAuditPlan] = useState<AdminPolicyPlanCode | null>(null)
  const [audits, setAudits] = useState<AdminPlanPolicyAudit[]>([])
  const [downloadPolicy, setDownloadPolicy] = useState<AdminShareDownloadPolicy | null>(null)
  const [downloadLimitDraft, setDownloadLimitDraft] = useState('')
  const [savingDownloadLimit, setSavingDownloadLimit] = useState(false)

  useEffect(() => { void load() }, [])

  async function load() {
    setLoading(true)
    setMessage('')
    try {
      const [next, nextDownloadPolicy] = await Promise.all([
        api.adminPolicies(),
        api.adminShareDownloadPolicy(),
      ])
      const sorted = [...next].sort((a, b) => PLAN_ORDER.indexOf(a.planCode) - PLAN_ORDER.indexOf(b.planCode))
      setPolicies(sorted)
      setDrafts(Object.fromEntries(sorted.map((policy) => [policy.planCode, toDraft(policy)])))
      setDownloadPolicy(nextDownloadPolicy)
      setDownloadLimitDraft(String(nextDownloadPolicy.maxDownloads))
    } catch (error) {
      setMessage(adminError(error))
    } finally {
      setLoading(false)
    }
  }

  function change(plan: AdminPolicyPlanCode, patch: Partial<PolicyDraft>) {
    setDrafts((current) => ({ ...current, [plan]: { ...current[plan], ...patch } }))
  }

  function payloadFor(plan: AdminPolicyPlanCode, draft: PolicyDraft): AdminPlanPolicyUpdatePayload {
    const weeklyFileRegistrationLimit = draft.weeklyFileUnlimited
      ? null
      : positiveInt(draft.weeklyFileRegistrationLimit, '주간 신규 음원 등록 수')
    const maxStoredFileSongs = draft.maxStoredFilesUnlimited
      ? null
      : positiveInt(draft.maxStoredFileSongs, '최대 저장 음원 수')
    const maxSegnosPerSong: number | null = null
    const dataRetentionAmount = positiveInt(draft.dataRetentionAmount, '세뇨 데이터 보관기간')
    const audioRetentionAmount = positiveInt(draft.audioRetentionAmount, '음원 보관기간')

    let fixedActiveShareLimit: number | null = null
    let shareRetentionAmount: number | null = null
    let shareRetentionUnit: AdminPolicyDurationUnit | null = null
    if (draft.activeShareLimitMode === 'FIXED') {
      fixedActiveShareLimit = positiveInt(draft.fixedActiveShareLimit, '동시 Active 공유 가능 곡 수')
    }
    if (draft.activeShareLimitMode !== 'DISABLED') {
      shareRetentionAmount = positiveInt(draft.shareRetentionAmount, '공유 링크 보관기간')
      shareRetentionUnit = draft.shareRetentionUnit
    }

    return {
      fileRegistrationWindowDays: weeklyFileRegistrationLimit == null ? null : 7,
      weeklyFileRegistrationLimit,
      maxStoredFileSongs,
      activeShareLimitMode: plan === 'UNLIMITED' ? 'USER' : draft.activeShareLimitMode,
      fixedActiveShareLimit: plan === 'UNLIMITED' ? null : fixedActiveShareLimit,
      dataRetentionAmount,
      dataRetentionUnit: draft.dataRetentionUnit,
      audioRetentionAmount,
      audioRetentionUnit: draft.audioRetentionUnit,
      shareRetentionAmount,
      shareRetentionUnit,
      maxSegnosPerSong,
      albumSharingEnabled: draft.albumSharingEnabled,
      ownerAdsEnabled: draft.ownerAdsEnabled,
    }
  }

  async function save(policy: AdminPlanPolicy) {
    const draft = drafts[policy.planCode]
    if (!draft) return
    let payload: AdminPlanPolicyUpdatePayload
    try {
      payload = payloadFor(policy.planCode, draft)
    } catch (error) {
      setMessage(adminError(error))
      return
    }
    if (!window.confirm(`${PLAN_LABEL[policy.planCode]} 요금제 정책을 변경하시겠습니까? 기존 데이터는 삭제하지 않고 이후 신규 등록/추가/공유 요청부터 새 정책이 적용됩니다.`)) return

    setSaving(policy.planCode)
    setMessage('')
    try {
      const updated = await api.adminUpdatePolicy(policy.planCode, payload)
      setPolicies((current) => current.map((item) => item.planCode === updated.planCode ? updated : item))
      setDrafts((current) => ({ ...current, [updated.planCode]: toDraft(updated) }))
      setMessage(`${PLAN_LABEL[policy.planCode]} 정책을 저장했습니다.`)
      if (auditPlan === policy.planCode) setAudits(await api.adminPolicyAudits(policy.planCode))
    } catch (error) {
      setMessage(adminError(error))
    } finally {
      setSaving(null)
    }
  }

  async function saveShareDownloadPolicy() {
    let maxDownloads: number
    try {
      maxDownloads = positiveInt(downloadLimitDraft, '공유 1건당 음원 다운로드 한도')
    } catch (error) {
      setMessage(adminError(error))
      return
    }
    if (!window.confirm(`공유 1건당 음원 다운로드 한도를 ${maxDownloads}회로 변경하시겠습니까? 기존 공유의 누적 사용 횟수는 유지됩니다.`)) return

    setSavingDownloadLimit(true)
    setMessage('')
    try {
      const updated = await api.adminUpdateShareDownloadPolicy(maxDownloads)
      setDownloadPolicy(updated)
      setDownloadLimitDraft(String(updated.maxDownloads))
      setMessage(`공유 다운로드 한도를 ${updated.maxDownloads}회로 저장했습니다.`)
    } catch (error) {
      setMessage(adminError(error))
    } finally {
      setSavingDownloadLimit(false)
    }
  }

  async function toggleAudits(plan: AdminPolicyPlanCode) {
    if (auditPlan === plan) {
      setAuditPlan(null)
      setAudits([])
      return
    }
    setMessage('')
    try {
      setAudits(await api.adminPolicyAudits(plan))
      setAuditPlan(plan)
    } catch (error) {
      setMessage(adminError(error))
    }
  }

  if (loading) return <section className="admin-card admin-message"><p>요금제 정책을 불러오고 있습니다.</p></section>

  return (
    <section className="admin-policy-section" aria-label="요금제 정책 관리">
      <div className="admin-policy-intro">
        <div>
          <h2>요금제 정책</h2>
          <p>OpenBeta를 포함한 요금제의 음원 등록·보관·공유 한도를 설정합니다. 세뇨 개수와 YouTube 등록은 전 요금제 공통 무제한입니다.</p>
        </div>
        <div className="admin-policy-safety">정책 변경으로 기존 곡·음원·세뇨를 즉시 삭제하지 않습니다.</div>
      </div>

      {message && <div className="admin-policy-message" role="status">{message}</div>}

      {downloadPolicy && (
        <article className="admin-policy-card admin-share-download-policy">
          <header>
            <div>
              <h3>공유 음원 다운로드</h3>
              <small>마지막 변경 {formatDate(downloadPolicy.updatedAt)}</small>
            </div>
            <span className="admin-badge">GLOBAL</span>
          </header>
          <div className="admin-policy-fields">
            <fieldset>
              <legend>공유 1건당 한도</legend>
              <label>최대 다운로드 횟수
                <input inputMode="numeric" value={downloadLimitDraft} onChange={(event) => setDownloadLimitDraft(event.target.value)} />
              </label>
              <small>FILE 음원 공유만 대상입니다. YouTube와 소유자 본인 재생은 카운트하지 않으며, 같은 공유를 중지했다 다시 켜도 누적 횟수는 유지됩니다.</small>
            </fieldset>
          </div>
          <footer className="admin-policy-actions">
            <button className="admin-button primary" type="button" disabled={savingDownloadLimit} onClick={() => void saveShareDownloadPolicy()}>
              {savingDownloadLimit ? '저장 중…' : '다운로드 한도 저장'}
            </button>
          </footer>
        </article>
      )}

      <div className="admin-policy-grid">
        {policies.map((policy) => {
          const draft = drafts[policy.planCode]
          if (!draft) return null
          const shareDisabled = draft.activeShareLimitMode === 'DISABLED'
          const unlimitedInternal = policy.planCode === 'UNLIMITED'
          return (
            <article className="admin-policy-card" key={policy.planCode}>
              <header>
                <div><h3>{PLAN_LABEL[policy.planCode]}</h3><small>마지막 변경 {formatDate(policy.updatedAt)}</small></div>
                <span className="admin-badge">{policy.planCode}</span>
              </header>

              <div className="admin-policy-fields">
                <fieldset>
                  <legend>신규 음원 등록</legend>
                  <label className="admin-policy-check"><input type="checkbox" checked={draft.weeklyFileUnlimited} onChange={(event) => change(policy.planCode, { weeklyFileUnlimited: event.target.checked })} /> 주간 등록 무제한</label>
                  <label>7일 최대 등록 곡 수<input inputMode="numeric" disabled={draft.weeklyFileUnlimited} value={draft.weeklyFileRegistrationLimit} onChange={(event) => change(policy.planCode, { weeklyFileRegistrationLimit: event.target.value })} /></label>
                  <label className="admin-policy-check"><input type="checkbox" checked={draft.maxStoredFilesUnlimited} onChange={(event) => change(policy.planCode, { maxStoredFilesUnlimited: event.target.checked })} /> 저장 음원 수 무제한</label>
                  <label>최대 저장 음원 수<input inputMode="numeric" disabled={draft.maxStoredFilesUnlimited} value={draft.maxStoredFileSongs} onChange={(event) => change(policy.planCode, { maxStoredFileSongs: event.target.value })} /></label>
                  <small>YouTube 신규 등록은 기존 정책대로 제한하지 않습니다.</small>
                </fieldset>

                <fieldset>
                  <legend>작업 데이터</legend>
                  <div className="admin-policy-duration"><label>세뇨 데이터 보관<input inputMode="numeric" value={draft.dataRetentionAmount} onChange={(event) => change(policy.planCode, { dataRetentionAmount: event.target.value })} /></label><select aria-label="세뇨 데이터 보관 단위" value={draft.dataRetentionUnit} onChange={(event) => change(policy.planCode, { dataRetentionUnit: event.target.value as AdminPolicyDurationUnit })}><option value="DAYS">일</option><option value="MONTHS">개월</option></select></div>
                  <small>곡당 세뇨 개수와 YouTube 등록 개수는 전 요금제에서 제한하지 않습니다.</small>
                </fieldset>

                <fieldset>
                  <legend>실제 음원 파일</legend>
                  <div className="admin-policy-duration"><label>음원 보관<input inputMode="numeric" value={draft.audioRetentionAmount} onChange={(event) => change(policy.planCode, { audioRetentionAmount: event.target.value })} /></label><select aria-label="음원 보관 단위" value={draft.audioRetentionUnit} onChange={(event) => change(policy.planCode, { audioRetentionUnit: event.target.value as AdminPolicyDurationUnit })}><option value="DAYS">일</option><option value="MONTHS">개월</option></select></div>
                  <small>음원 보관기간이 먼저 끝나도 세뇨 데이터는 별도 보관기간까지 유지됩니다.</small>
                </fieldset>

                <fieldset>
                  <legend>공유</legend>
                  <label>공유 방식
                    <select disabled={unlimitedInternal} value={unlimitedInternal ? 'USER' : draft.activeShareLimitMode} onChange={(event) => change(policy.planCode, { activeShareLimitMode: event.target.value as AdminActiveShareLimitMode })}>
                      {!unlimitedInternal && <option value="DISABLED">공유 불가</option>}
                      {!unlimitedInternal && <option value="FIXED">고정 한도</option>}
                      {unlimitedInternal && <option value="USER">사용자별 한도</option>}
                    </select>
                  </label>
                  {draft.activeShareLimitMode === 'FIXED' && <label>동시 Active 공유 곡 수<input inputMode="numeric" value={draft.fixedActiveShareLimit} onChange={(event) => change(policy.planCode, { fixedActiveShareLimit: event.target.value })} /></label>}
                  {!shareDisabled && <div className="admin-policy-duration"><label>공유 링크 보관<input inputMode="numeric" value={draft.shareRetentionAmount} onChange={(event) => change(policy.planCode, { shareRetentionAmount: event.target.value })} /></label><select aria-label="공유 링크 보관 단위" value={draft.shareRetentionUnit} onChange={(event) => change(policy.planCode, { shareRetentionUnit: event.target.value as AdminPolicyDurationUnit })}><option value="DAYS">일</option><option value="MONTHS">개월</option></select></div>}
                </fieldset>

                <fieldset className="admin-policy-flags">
                  <legend>기타 정책</legend>
                  <label className="admin-policy-check"><input type="checkbox" checked={draft.albumSharingEnabled} onChange={(event) => change(policy.planCode, { albumSharingEnabled: event.target.checked })} /> 앨범 공유 허용</label>
                  <label className="admin-policy-check"><input type="checkbox" checked={draft.ownerAdsEnabled} onChange={(event) => change(policy.planCode, { ownerAdsEnabled: event.target.checked })} /> 소유자 연습 광고 표시</label>
                </fieldset>
              </div>

              <footer className="admin-policy-actions">
                <button className="admin-button primary" type="button" disabled={saving === policy.planCode} onClick={() => void save(policy)}>{saving === policy.planCode ? '저장 중…' : '정책 저장'}</button>
                <button className="admin-button" type="button" onClick={() => void toggleAudits(policy.planCode)}>{auditPlan === policy.planCode ? '변경 이력 닫기' : '변경 이력'}</button>
              </footer>

              {auditPlan === policy.planCode && <div className="admin-policy-audits">
                {audits.length === 0 ? <p>변경 이력이 없습니다.</p> : audits.map((audit) => <article key={audit.id}><strong>{formatDate(audit.createdAt)}</strong><small>운영자 {audit.actorCentralUserId}</small><code>{audit.beforeValue}</code><span>→</span><code>{audit.afterValue}</code></article>)}
              </div>}
            </article>
          )
        })}
      </div>
    </section>
  )
}
